import { useState, useEffect, useRef } from 'react';
import { supabase } from '../config/supabaseClient';

// Simple debounced search hook that queries Supabase with ilike on name and short_name
export function useProductSearch(query, { limit = 8, delay = 300, sessionKey } = {}) {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const mounted = useRef(true);
  const timer = useRef(null);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    if (!query || query.trim().length === 0) {
      setResults([]);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try {
        // Query name and short_name using ilike (case-insensitive)
        const q = `%${query.replace(/%/g, '')}%`;
        const { data, error: fetchError } = await supabase
          .from('products')
          .select('id, name, short_name, image, images, price')
          .or(`name.ilike.${q},short_name.ilike.${q}`)
          .limit(limit);

        if (fetchError) {
          console.warn('Search fetch error:', fetchError);
          if (!mounted.current) return;
          setError(fetchError.message || String(fetchError));
          setResults([]);
        } else {
          if (!mounted.current) return;
          // Map to expected shape
          const mapped = (data || []).map(row => ({
            id: Number(row.id),
            name: row.name,
            shortName: row.short_name || '',
            image: row.image || (row.images && row.images[0]) || '',
            price: Number(row.price) || 0,
          }));
          setResults(mapped);
        }
      } catch (err) {
        console.error('Error during product search:', err);
        if (!mounted.current) return;
        setError(err.message || String(err));
        setResults([]);
      } finally {
        if (!mounted.current) return;
        setLoading(false);
      }
    }, delay);

    return () => { if (timer.current) clearTimeout(timer.current); };
  // include sessionKey so callers can force the hook to reset when sessionKey changes
  }, [query, limit, delay, sessionKey]);

  return { results, loading, error };
}
